/**
 * Returns a date string in YYYY-MM-DD format relative to the current date
 * @param days Number of days to add (positive) or subtract (negative) from current date
 * @returns ISO date string (YYYY-MM-DD)
 */
export const getRelativeDate = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return formatISODate(date);
};

/**
 * Returns the current date in YYYY-MM-DD format
 * @returns ISO date string (YYYY-MM-DD)
 */
export const getCurrentDate = (): string => {
  return formatISODate(new Date());
};

/**
 * Formats a date as DD/MM/YYYY in the user's locale
 * @param date Date to format
 * @returns Formatted date string or empty string if date is invalid
 */
export const formatDate = (date: Date): string => {
  if (!date || isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('en-GB').format(date);
};

/**
 * Returns the first day of the current month in YYYY-MM-DD format
 * @returns ISO date string (YYYY-MM-DD)
 */
export const getFirstDayOfMonth = (): string => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

/**
 * Returns the previous calendar month as a closed range, first day through last day.
 * Rolls back across the year boundary (January -> December of the previous year).
 * @returns Start and end ISO date strings (YYYY-MM-DD)
 */
export const getPreviousMonthRange = (): { start: string; end: string } => {
  const today = new Date();
  const firstDayOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  // Day 0 of the current month is the last day of the previous month.
  const lastDayOfPreviousMonth = new Date(today.getFullYear(), today.getMonth(), 0);

  return {
    start: formatISODate(firstDayOfPreviousMonth),
    end: formatISODate(lastDayOfPreviousMonth),
  };
};

/**
 * Returns the first day of the current year in YYYY-MM-DD format
 * @returns ISO date string (YYYY-MM-DD)
 */
export const getFirstDayOfYear = (): string => {
  const date = new Date();
  return `${date.getFullYear()}-01-01`;
};

/** Inclusive 90-day window (today + 89 days back) used as the transactions table default. */
export const getDefaultTransactionsDateRange = (): { start: string; end: string } => ({
  start: getRelativeDate(-89),
  end: getCurrentDate(),
});

/**
 * Formats a Date object into ISO format string (YYYY-MM-DD)
 * @param date Date to format
 * @returns ISO date string (YYYY-MM-DD)
 */
export const formatISODate = (date: Date): string => {
  if (!date || isNaN(date.getTime())) {
    return '';
  }
  // The year is padded like the rest: <input type="date"> rejects a value whose year is
  // not four digits, so an implausible date used to render as an empty field - exactly the
  // "nothing was entered" illusion this flow is meant to dispel (SPR-1179).
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formats a date as DD/MM/YYYY in the specified locale
 * @param date Date string or Date object to format
 * @param locale Locale string (e.g., 'he', 'en'). Defaults to 'he-IL'
 * @returns Formatted date string (DD/MM/YYYY)
 */
/**
 * "2.4.2026" — לא מרופד, בדיוק כמו Format.date ב-hatzdaka-nedarim
 * (`new Date(value).toLocaleDateString('he-IL')`, בלי אפשרויות).
 *
 * ⚠️ **לא formatDateLocale.** זה לא רק העדפה — "02.04.2026" הארוך
 * יותר בפיקסלים בודדים היה מספיק כדי שתאריך+סוג+מזהה בשורת המטא של
 * כרטיס בקשת תמיכה לא ייכנסו בשורה אחת וישברו לשתי שורות, בזמן
 * שבנדרים הם נכנסים. משתמשים בזה רק בשורת המטא של הכרטיסים
 * המצומצמים (HAT-1170) — לא בטבלת הדסקטופ ולא במגירת הפרטים, ששם
 * formatDateLocale/הפורמט המרופד הוא המוסכמה הקיימת של האתר ואין
 * סיבה לשבור אותה.
 */
export const formatDateShort = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!dateObj || isNaN(dateObj.getTime())) {
    return '';
  }
  return dateObj.toLocaleDateString('he-IL');
};

export const formatDateLocale = (date: string | Date, locale: string = 'he-IL'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!dateObj || isNaN(dateObj.getTime())) {
    return '';
  }

  return Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(dateObj);
};

/**
 * Formats a date as DD/MM/YYYY HH:MM in the specified locale
 * @param date Date string or Date object to format
 * @param locale Locale string (e.g., 'he-IL', 'en-GB'). Defaults to 'he-IL'
 * @returns Formatted date string (DD/MM/YYYY HH:MM)
 */
export const formatDateTimeLocale = (date: string | Date, locale: string = 'he-IL'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!dateObj || isNaN(dateObj.getTime())) {
    return '';
  }

  return Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj);
};

/**
 * Parse a board date without throwing. Empty, unparseable, and SQL DateTime.MinValue
 * (year 1) are treated as missing so the cell can show a placeholder instead of a blank rail.
 *
 * A merely implausible date - a birth date typed as year 0015, say - is NOT treated as
 * missing (SPR-1179). Hiding it made coordinators believe nothing had been entered when a
 * wrong value was in fact on record, so it is returned as-is and the caller decides how to
 * present it; use `isPlausibleCivilDate` to tell the two apart.
 *
 * Civil dates (the printed Gregorian / Hebrew / <time dateTime>) are always
 * Asia/Jerusalem. A date-only `YYYY-MM-DD` is that Jerusalem day, not UTC midnight.
 */
const SQL_MIN_VALUE_YEAR = 1;
export const MIN_PLAUSIBLE_YEAR = 1900;
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export const TRANSACTION_CIVIL_TIMEZONE = 'Asia/Jerusalem';

export const parseTransactionDate = (value: unknown): Date | null => {
  if (value == null || value === '') return null;

  if (typeof value === 'string') {
    const dateOnly = DATE_ONLY.exec(value.trim());
    if (dateOnly) {
      const year = Number(dateOnly[1]);
      const month = Number(dateOnly[2]);
      const day = Number(dateOnly[3]);
      if (year <= SQL_MIN_VALUE_YEAR) return null;
      // Noon UTC is still that civil day in Jerusalem (UTC+2/+3). Date.UTC maps years
      // 0-99 onto 1900-1999, so a year like 0015 must be set explicitly or it silently
      // becomes 1915.
      const dateOnlyValue = new Date(Date.UTC(2000, month - 1, day, 12, 0, 0));
      dateOnlyValue.setUTCFullYear(year);
      return dateOnlyValue;
    }
  }

  const date = value instanceof Date ? new Date(value.getTime()) : new Date(String(value));
  if (Number.isNaN(date.getTime())) return null;
  if (transactionCivilParts(date).year <= SQL_MIN_VALUE_YEAR) return null;
  return date;
};

/**
 * Whether a parsed date is a real calendar date someone could have meant, as opposed to a
 * typo that landed centuries off (year 0015 for 2015). Implausible dates are still shown -
 * see `parseTransactionDate` - but are worth marking as suspect.
 */
export const isPlausibleCivilDate = (date: Date): boolean =>
  transactionCivilParts(date).year >= MIN_PLAUSIBLE_YEAR;

/** Earliest birth date the system accepts, as a `<input type="date">` min value (SPR-1179). */
export const MIN_BIRTH_DATE_INPUT = `${MIN_PLAUSIBLE_YEAR}-01-01`;

/**
 * Whether a birth date is within the accepted range. An empty value is *not* this function's
 * concern - "a birth date is required" is a separate rule, checked separately, so that the two
 * produce their own messages.
 */
export const isBirthDateInAllowedRange = (value: Date | string | null | undefined): boolean => {
  if (value == null || value === '') return true;
  const date = parseTransactionDate(value);
  return date != null && isPlausibleCivilDate(date);
};

export type TransactionCivilParts = {
  year: number;
  month: number;
  day: number;
  iso: string;
};

export const transactionCivilParts = (date: Date): TransactionCivilParts => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TRANSACTION_CIVIL_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  return { year: Number(year), month: Number(month), day: Number(day), iso: `${year}-${month}-${day}` };
};

export const transactionFullDateFormat = new Intl.DateTimeFormat('he-IL', {
  timeZone: TRANSACTION_CIVIL_TIMEZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
export const transactionShortDateFormat = new Intl.DateTimeFormat('he-IL', {
  timeZone: TRANSACTION_CIVIL_TIMEZONE,
  day: '2-digit',
  month: '2-digit',
});

export { formatHebrewTransactionDate } from './hebrewDateHelper';
